import { z } from 'zod';
import { workoutTypeSchema } from './training.js';
/**
 * A batch of health records read on the phone (HealthKit or Health Connect)
 * and posted by the app (M-72). On-device sources are not providers
 * (ADR-019): nothing connects, the phone reads and pushes. Figures are the
 * device's own; nothing here is an LLM estimate.
 */
export const healthBatchWorkoutSchema = z.object({
    /** The platform's id for the session, so a re-send is not a second session. */
    externalId: z.string().min(1).max(200),
    type: workoutTypeSchema,
    startedAt: z.string().datetime(),
    durationS: z.number().int().positive().max(86400),
    distanceM: z.number().min(0).nullish(),
    energyKj: z.number().min(0).nullish(),
});
/** One local day's readings (ADR-010); a field the device did not report is left out. */
export const healthBatchMetricSchema = z.object({
    date: z.string().date(),
    steps: z.number().int().min(0).max(200000).nullish(),
    distanceM: z.number().min(0).max(1_000_000).nullish(),
    restingHr: z.number().int().min(20).max(150).nullish(),
    sleepMin: z.number().int().min(0).max(1440).nullish(),
    sleepDeepMin: z.number().int().min(0).max(1440).nullish(),
    sleepLightMin: z.number().int().min(0).max(1440).nullish(),
    sleepRemMin: z.number().int().min(0).max(1440).nullish(),
    activeEnergyKj: z.number().min(0).nullish(),
    weightKg: z.number().min(20).max(400).nullish(),
    bodyFatPct: z.number().min(1).max(75).nullish(),
    /** Blood oxygen. Below 70 is not a reading a wrist sensor produces; it is a bad one. */
    spo2Pct: z.number().int().min(70).max(100).nullish(),
    /**
     * The day's heart rate as measured (ADR-025): lowest, mean, highest.
     *
     * Separate from `restingHr`, which is a claim about a state rather than a
     * record of the day, and which the app may have worked out itself.
     */
    hrMinBpm: z.number().int().min(25).max(250).nullish(),
    hrAvgBpm: z.number().int().min(25).max(250).nullish(),
    hrMaxBpm: z.number().int().min(25).max(250).nullish(),
    /**
     * True when `restingHr` was worked out from the day's samples rather than
     * published by the device (ADR-025), so the app can say which it is
     * showing rather than passing ours off as the band's.
     */
    restingHrDerived: z.boolean().nullish(),
});
/**
 * One app's sleep for one day, as the main sleep and its naps (ADR-034).
 * Kept per app so the person's preferred source can be chosen later.
 */
export const healthBatchSleepNightSchema = z.object({
    date: z.string().date(),
    /** The app that recorded it, as the health store names it; null if it did not say. */
    app: z.string().max(200).nullable(),
    sleepMin: z.number().int().min(0).max(1440),
    napMin: z.number().int().min(0).max(1440).default(0),
    deepMin: z.number().int().min(0).max(1440).nullish(),
    lightMin: z.number().int().min(0).max(1440).nullish(),
    remMin: z.number().int().min(0).max(1440).nullish(),
    hasStages: z.boolean().default(false),
});
export const healthBatchSchema = z.object({
    memberId: z.string().uuid(),
    provider: z.enum(['healthkit', 'health_connect']),
    workouts: z.array(healthBatchWorkoutSchema).max(200).default([]),
    metrics: z.array(healthBatchMetricSchema).max(200).default([]),
    /**
     * Sleep by app (ADR-034). A build that sends this sends no sleep in
     * `metrics`; an older build sends `metrics[].sleepMin` and nothing here.
     */
    sleepNights: z.array(healthBatchSleepNightSchema).max(600).default([]),
});
export const healthBatchResultSchema = z.object({
    workoutsImported: z.number().int(),
    workoutsMerged: z.number().int(),
    metricsWritten: z.number().int(),
});
//# sourceMappingURL=sync.js.map